import { Button } from '@/components/ui/button';
import AppEnv from '@/config/config';
import useNetwork from '@/hooks/use-network';
import LocalStorage from '@/lib/local-storage';
import { formatNumberToCurrency } from '@/lib/utils';
import { FullScreenLoadingContext } from '@/pages/_providers/full-loading-overlay-provider';
import { useMutation } from '@tanstack/react-query';
import { useContext, useRef, useState } from 'react';
import Resizer from 'react-image-file-resizer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

const SignaturePad = SignatureCanvas as any;

export default function SignaturePage() {
  const navigate = useNavigate();
  const { isOnline } = useNetwork();
  const signatureRef = useRef<SignaturePad>(null);
  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get('transaction_id');
  const price = searchParams.get('price');
  const { event: eventFullScreenLoading } = useContext(
    FullScreenLoadingContext,
  );
  const accessToken = LocalStorage.getToken();

  const [isEmpty, setIsEmpty] = useState(true);

  const clearSignature = () => {
    signatureRef.current?.clear();
    setIsEmpty(true);
  };

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: async (data: File) => {
      const formData = new FormData();
      formData.append('file', data);
      formData.append('transaction_id', transactionId!);

      const response = await fetch(AppEnv.get().apiRest, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const responseData = await response.json();
      return responseData;
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resizeFile = (file: any) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        300,
        150,
        'png',
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        'file',
      );
    });

  const handleDone = async () => {
    try {
      eventFullScreenLoading.setIsLoading(true);
      const signatureData = signatureRef.current?.toDataURL('image/png');
      const blob = await fetch(signatureData!).then((res) => res.blob());
      const file = new File([blob], 'signature.png', { type: 'image/png' });
      const image = (await resizeFile(file)) as File;
      await uploadFile(image);
      navigate(`/gift-card/receipt/${transactionId}`);
    } catch (error) {
      eventFullScreenLoading.setIsLoading(false);
    } finally {
      eventFullScreenLoading.setIsLoading(false);
    }
  };

  return (
    <div className="flex size-full flex-col items-center justify-center p-24">
      <div className="flex size-full flex-col">
        <div className="mb-8 flex items-start justify-between">
          <h1 className="text-5xl font-semibold text-gray-800">Signature</h1>
          <div className="text-right">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Total Amount:</span>
              <span className="text-5xl font-semibold">
                {formatNumberToCurrency(price)}
              </span>
            </div>
          </div>
        </div>
        <p className="text-xl text-gray-600">
          Transaction approved. Please sign your name.
        </p>
        <SignaturePad
          ref={signatureRef}
          canvasProps={{
            className: 'mt-8 h-full w-full rounded-lg bg-gray-50',
          }}
          onEnd={() => setIsEmpty(false)}
        />
        <div className="mt-8 flex gap-4">
          <Button
            variant="outline"
            onClick={() => clearSignature()}
            className="h-16 flex-1 rounded-md text-xl font-semibold text-gray-600"
          >
            Clear
          </Button>
          <Button
            onClick={handleDone}
            disabled={isEmpty || !isOnline}
            className="h-16 flex-1 rounded-md bg-blue-500 text-xl font-semibold text-white hover:bg-blue-600"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
